import { useCallback, useEffect, useState } from "react";
import { FileText, SlidersHorizontal } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { usePoll } from "@/lib/live";
import { METRICS } from "@/lib/metrics";
import { exportSections } from "@/lib/exporter";
import { useSettings } from "@/context/SettingsContext";
import { rupiah, todayStr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const PERIODS = { semua: "Semua waktu", hari: "Hari ini", bulan: "Bulan ini" };
const rangeOf = (p) => (p === "hari" ? { start: todayStr(), end: todayStr() } : p === "bulan" ? { start: `${todayStr().slice(0, 7)}-01`, end: todayStr() } : {});
const fmt = (k, v) => (METRICS[k].money ? rupiah(v) : Number(v).toLocaleString("id-ID"));

const Cards = ({ rows }) => (
  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="metrics-cards">
    {rows.map(([k, v]) => (
      <div key={k} className="rounded-3xl border bg-card p-5 shadow-sm" data-testid={`metric-${k}`}>
        <p className="text-xs text-muted-foreground">{METRICS[k].label}</p>
        <p className="mt-2 font-heading text-xl sm:text-2xl font-bold font-mono truncate">{fmt(k, v)}</p>
      </div>
    ))}
  </div>
);

const TableView = ({ rows }) => (
  <div className="panel !p-0 overflow-hidden" data-testid="metrics-table">
    <table className="w-full text-sm">
      <tbody>{rows.map(([k, v]) => (
        <tr key={k} className="border-b last:border-0" data-testid={`metric-${k}`}>
          <td className="py-3 pl-5 text-muted-foreground">{METRICS[k].label}</td>
          <td className="py-3 pr-5 text-right font-mono font-semibold">{fmt(k, v)}</td>
        </tr>
      ))}</tbody>
    </table>
  </div>
);

export const MetricsBoard = ({ title, editPath }) => {
  const { settings } = useSettings();
  const [period, setPeriod] = useState("semua");
  const [layout, setLayout] = useState(null);
  const [vals, setVals] = useState(null);
  const load = useCallback(() => { api.get("/metrics", { params: rangeOf(period) }).then((r) => setVals(r.data.values)).catch(() => {}); }, [period]);
  useEffect(() => { api.get("/metrics/layout").then((r) => setLayout(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  usePoll(load, 15000);
  if (!layout || !vals) return <p className="text-muted-foreground mb-6">Memuat ringkasan...</p>;
  const rows = layout.items.filter((i) => i.visible && i.key in vals).map((i) => [i.key, vals[i.key]]);
  const pdf = () => exportSections("pdf", { title: `${settings.app_name} — ${title}`, subtitle: `Periode: ${PERIODS[period]} · dicetak ${todayStr()}`, filename: `${title.toLowerCase().replace(/\s+/g, "-")}-${todayStr()}`,
    sections: [{ title, head: ["Keterangan", "Nilai"], rows: rows.map(([k, v]) => [METRICS[k].label, fmt(k, v)]) }] });
  return (
    <section className="mb-8" data-testid="metrics-board">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-heading text-lg font-bold">{title}</h2>
        <div className="flex flex-wrap gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-10 w-[150px] rounded-full" data-testid="metrics-period"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(PERIODS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
          {editPath && <Button asChild variant="outline" className="h-10 rounded-full"><Link to={editPath} data-testid="metrics-edit-link"><SlidersHorizontal className="h-4 w-4 mr-1.5" />Atur</Link></Button>}
          <Button variant="outline" className="h-10 rounded-full" onClick={pdf} disabled={!rows.length} data-testid="metrics-export-pdf"><FileText className="h-4 w-4 mr-1.5" />Export PDF</Button>
        </div>
      </div>
      {rows.length === 0 ? <p className="panel text-sm text-muted-foreground text-center" data-testid="metrics-empty">Semua data disembunyikan. Atur tampilan untuk menampilkannya.</p>
        : layout.mode === "tabel" ? <TableView rows={rows} /> : <Cards rows={rows} />}
    </section>
  );
};
